import React from "react";
import convertNumbThousand from "../../utils/convertNumbThousand";
import { formatNumber } from "../../utils/convertToNumber";

const iconProps = { width: 24, height: 24 };

const Icons = {
  wallet: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 16 16"
      {...iconProps}
    >
      <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z" />
    </svg>
  ),
  dollar: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...iconProps}
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  serviceCharge: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...iconProps}>
      <path
        d="M6,11a1,1,0,1,0,1,1A1,1,0,0,0,6,11Zm5.86-1.55h0L4.71,2.29A1,1,0,0,0,3.29,3.71L4.59,5H4A3,3,0,0,0,1,8v8a3,3,0,0,0,3,3H18.59l2.7,2.71a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Zm-.74,2.09,1.34,1.34A1,1,0,0,1,12,13a1,1,0,0,1-1-1A1,1,0,0,1,11.12,11.54ZM4,17a1,1,0,0,1-1-1V8A1,1,0,0,1,4,7H6.59l3.1,3.1A3,3,0,0,0,9,12a3,3,0,0,0,3,3,3,3,0,0,0,1.9-.69L16.59,17ZM20,5H12.66a1,1,0,0,0,0,2H20a1,1,0,0,1,1,1v7.34a1,1,0,1,0,2,0V8A3,3,0,0,0,20,5Zm-1,7a1,1,0,1,0-1,1A1,1,0,0,0,19,12Z"
        fill="currentColor"
      />
    </svg>
  ),
  ticket: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1"
      {...iconProps}
    >
      <path d="M19,7V19a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V7A2,2,0,0,1,7,5h5l2-2h4a2,2,0,0,1,2,2Z" />
      <polyline points="16 2 16 8 22 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  ),
  creditCard: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 16 16"
      {...iconProps}
    >
      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1H0V4zm0 3v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7H0zm3 2h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1z" />
    </svg>
  ),
  user: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1"
      {...iconProps}
    >
      <path d="M12,6a3,3,0,1,0,3,3A3,3,0,0,0,12,6Zm0,4a1,1,0,1,1,1-1A1,1,0,0,1,12,10Z" />
      <path d="M12,14a5,5,0,0,0-5,5v1a1,1,0,0,0,1,1H17a1,1,0,0,0,1-1V19A5,5,0,0,0,12,14Zm0,8a3,3,0,0" />
    </svg>
  ),
};

export function getBillingStatGroups(billings) {
  const r = billings?.result;
  const r0 = r?.[0];
  const r1 = r?.[1];

  const stat = (title, data, icon, color) => ({ title, data, icon, color });

  return [
    {
      id: "overview",
      title: "Key Metrics",
      subtitle:
        "Total Sales, Total System Fare, Total Sold Tickets, Total Visa Sales, Corporate Sales, Total Active Agents",
      variant: "overview",
      defaultOpen: true,
      badge: 5,
      stats: [
        stat(
          "Total Sales",
          convertNumbThousand(r1?.totalAmount || 0) + " birr",
          Icons.dollar,
          "success"
        ),
        stat(
          "Total System Fare",
          formatNumber(`${r1?.price?.toFixed(2) ?? "0.00"}`) + " birr",
          Icons.dollar,
          "danger"
        ),
        stat(
          "Total Sold Tickets",
          formatNumber(billings?.ticketCount) || 0,
          Icons.ticket,
          "warning"
        ),
        stat(
          "Total Visa Sales",
          billings?.visaTicketCount || 0,
          Icons.user,
          "warning"
        ),
        stat(
          "Corporate Sales",
          convertNumbThousand(billings?.creditAmount) + " birr",
          Icons.creditCard,
          "warning"
        ),
        stat(
          "Total Active Agents",
          billings?.totalAgents || 0,
          Icons.user,
          "warning"
        ),
      ],
    },
    {
      id: "revenue",
      title: "Revenue Breakdown",
      gridGroup: "revenue",
      subtitle: "Total Service Charge, System Fee, Total Void Fee, Net Revenue",
      variant: "default",
      defaultOpen: false,
      badge: 5,
      stats: [
        stat(
          "Total Service Charge",
          convertNumbThousand(r1?.serviceCharge) + " birr",
          Icons.serviceCharge,
          "success"
        ),
        stat(
          "System Fee",
          convertNumbThousand(r1?.systemFee) + " birr",
          Icons.dollar,
          "success"
        ),
        stat(
          "Total Void Fee",
          (formatNumber(r1?.voidSC ?? 0) || 0) + " birr",
          Icons.dollar,
          "danger"
        ),
        stat(
          "Net Revenue",
          convertNumbThousand(r1?.netRevenue) + " birr",
          Icons.dollar,
          "danger"
        ),
      ],
    },
    {
      id: "operations",
      title: "Ticketing Operations",
      gridGroup: "revenue",
      subtitle: "Post-sale ticket activity",
      variant: "default",
      defaultOpen: false,
      badge: 3,
      stats: [
        stat(
          "Total Voided Tickets",
          formatNumber(billings?.voidedTicketCount) || 0,
          Icons.dollar,
          "danger"
        ),
        stat(
          "Total Refunded Tickets",
          formatNumber(billings?.refundedTicketCount) || 0,
          Icons.dollar,
          "danger"
        ),
        stat(
          "Total Exchanged Tickets",
          formatNumber(billings?.exchangeTicketCount) || 0,
          Icons.dollar,
          "danger"
        ),
      ],
    },
    {
      id: "ndc",
      gridGroup: "channels",
      title: "NDC",
      subtitle: "New Distribution Capability metrics",
      variant: "ndc",
      defaultOpen: false,
      badge: 3,
      stats: [
        stat(
          "NDC Base Price",
          formatNumber(`${r1?.NDCPrice?.toFixed(2) ?? "0.00"}`) + " birr",
          Icons.dollar,
          "danger"
        ),
        stat(
          "NDC Service Charge",
          convertNumbThousand(r1?.NDCServiceCharge) + " birr",
          Icons.serviceCharge,
          "success"
        ),
        stat(
          "Total Sold NDC Tickets",
          formatNumber(billings?.NDCTicketCount) || 0,
          Icons.ticket,
          "warning"
        ),
      ],
    },
    {
      id: "amadeus",
      gridGroup: "channels",
      title: "Amadeus",
      subtitle: "Amadeus GDS metrics",
      variant: "amadeus",
      defaultOpen: false,
      badge: 3,
      stats: [
        stat(
          "Amadeus Base Price",
          formatNumber(`${r1?.AmadeusPrice?.toFixed(2) ?? "0.00"}`) + " birr",
          Icons.dollar,
          "danger"
        ),
        stat(
          "Amadeus Service Charge",
          convertNumbThousand(r1?.AmadeusServiceCharge) + " birr",
          Icons.serviceCharge,
          "success"
        ),
        stat(
          "Total Sold Amadeus Tickets",
          formatNumber(billings?.AmadeusTicketCount) || 0,
          Icons.ticket,
          "warning"
        ),
      ],
    },
    {
      id: "airarabia",
      gridGroup: "channels",
      title: "Air Arabia",
      subtitle: "Air Arabia specific metrics",
      variant: "airarabia",
      defaultOpen: false,
      badge: 3,
      stats: [
        stat(
          "Air Arabia Base Price",
          formatNumber(`${r1?.AirArabiaPrice?.toFixed(2) ?? "0.00"}`) + " birr",
          Icons.dollar,
          "danger"
        ),
        stat(
          "Air Arabia Service Charge",
          convertNumbThousand(r1?.AirArabiaServiceCharge) + " birr",
          Icons.serviceCharge,
          "success"
        ),
        stat(
          "Total Sold Air Arabia Tickets",
          formatNumber(billings?.AirArabiaTicketCount) || 0,
          Icons.ticket,
          "warning"
        ),
      ],
    },
    {
      id: "credit",
      gridGroup: "operations",
      title: "Credit & Financial",
      subtitle:
        "Total Deposits, Short-term Credit, Fare Difference, Total Tax and Penalty",
      variant: "default",
      defaultOpen: false,
      badge: 5,
      stats: [
        stat(
          "Total Deposits Amount",
          formatNumber(`${r0?.wallet?.toFixed(2) ?? "0.00"}`) + " birr",
          Icons.wallet,
          "primary"
        ),
        stat(
          "Total Short-term Credit",
          convertNumbThousand(r0?.STCredit) + " birr",
          Icons.creditCard,
          "warning"
        ),
        stat(
          "Fare Difference",
          formatNumber(r1?.fareDifference) || 0,
          Icons.dollar,
          "danger"
        ),
        stat(
          "Total Tax and Penalty",
          convertNumbThousand(r1?.taxandpenalty) + " birr",
          Icons.dollar,
          "danger"
        ),
      ],
    },
  ];
}
